import { Suspense, type ComponentType } from "react";
import { Navigate } from "react-router-dom";
import IRoute from "./IRoute";
import UnauthenticatedRoute from "./UnauthenticatedRoute";
import ProtectedRoute from "./ProtectedRoute";
import ErrorBoundary from "../components/ErrorBoundary/ErrorBoundary";
import Loader from "../components/Loader/Loader";

/**
 * Every route component is `React.lazy`-wrapped (Phase 8) so the
 * entire page-level UI lives behind a Suspense boundary. The fallback
 * is the same `<Loader />` used by the auth bootstrap — users see a
 * single, recognisable spinner whether the app is hydrating, the route
 * is fetching its chunk, or auth is resolving.
 */
export default function RouteLogic({ route }: { route: IRoute }) {
  const Component = route.component as ComponentType;

  if (route.redirect) {
    return <Navigate to={route.redirect} replace />;
  }

  const body = <Component />;

  if (route.needAuth === false) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loader />}>
          <UnauthenticatedRoute>{body}</UnauthenticatedRoute>
        </Suspense>
      </ErrorBoundary>
    );
  }
  if (route.needAuth === true) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loader />}>
          <ProtectedRoute role={route.role}>{body}</ProtectedRoute>
        </Suspense>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      <Suspense fallback={<Loader />}>{body}</Suspense>
    </ErrorBoundary>
  );
}
